import type { SoundCue } from "./protocol.js";

/**
 * Tiny procedural sound engine (idea 8). WebAudio oscillators + gain envelopes —
 * no asset files, no bundle bloat. Deliberately sparse: only discrete UI/run
 * events play (never per-shot/per-hit), with a low master volume + a debounce so
 * the swarm can't machine-gun the speakers. Lazily creates the AudioContext on
 * the first user gesture (browsers block audio before that).
 */

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let muted = false;
const MASTER_VOLUME = 0.18; // intentionally quiet ("not overwhelming")

/** Per-cue debounce: ignore the same cue if it fired within this window (ms). */
const DEBOUNCE_MS = 90;
const lastPlayed = new Map<SoundCue, number>();

function ensureContext(): AudioContext | null {
  if (typeof AudioContext === "undefined") return null;
  if (ctx === null) {
    ctx = new AudioContext();
    master = ctx.createGain();
    master.gain.value = muted ? 0 : MASTER_VOLUME;
    master.connect(ctx.destination);
  }
  // Browsers start the context suspended until a gesture resumes it.
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

/** One enveloped oscillator tone. */
function tone(
  ac: AudioContext,
  startAt: number,
  freq: number,
  durS: number,
  type: OscillatorType,
  peak: number,
): void {
  if (master === null) return;
  const osc = ac.createOscillator();
  const g = ac.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, startAt);
  g.gain.setValueAtTime(0.0001, startAt);
  g.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), startAt + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, startAt + durS);
  osc.connect(g);
  g.connect(master);
  osc.start(startAt);
  osc.stop(startAt + durS + 0.02);
}

/** A short rising/falling note sequence (for chimes/stings). */
function sequence(
  ac: AudioContext,
  notes: readonly { freq: number; at: number; dur: number; type?: OscillatorType }[],
  peak: number,
): void {
  const t0 = ac.currentTime;
  for (const n of notes) tone(ac, t0 + n.at, n.freq, n.dur, n.type ?? "triangle", peak);
}

/** Synthesize + play a cue. Each cue is a tiny hand-tuned envelope. */
export function playSound(cue: SoundCue): void {
  if (muted) return;
  const now = performance.now();
  const debounce = cue === "shoot" ? 130 : DEBOUNCE_MS; // shots throttled harder (no buzz)
  if (now - (lastPlayed.get(cue) ?? -Infinity) < debounce) return;
  lastPlayed.set(cue, now);
  const ac = ensureContext();
  if (ac === null) return;

  switch (cue) {
    case "shoot":
      // Soft, quiet pew — quieter than the event cues so auto-fire isn't fatiguing.
      sequence(ac, [{ freq: 660, at: 0, dur: 0.05, type: "square" }], 0.25);
      break;
    case "ping":
      // Two-note "blip" — bright, short.
      sequence(
        ac,
        [
          { freq: 880, at: 0, dur: 0.09, type: "sine" },
          { freq: 1320, at: 0.06, dur: 0.1, type: "sine" },
        ],
        0.5,
      );
      break;
    case "levelup":
      // Ascending major-ish arpeggio.
      sequence(
        ac,
        [
          { freq: 523, at: 0, dur: 0.12 },
          { freq: 659, at: 0.08, dur: 0.12 },
          { freq: 784, at: 0.16, dur: 0.16 },
        ],
        0.6,
      );
      break;
    case "start":
      sequence(
        ac,
        [
          { freq: 392, at: 0, dur: 0.12 },
          { freq: 587, at: 0.1, dur: 0.18 },
        ],
        0.6,
      );
      break;
    case "victory":
      sequence(
        ac,
        [
          { freq: 523, at: 0, dur: 0.14 },
          { freq: 659, at: 0.12, dur: 0.14 },
          { freq: 784, at: 0.24, dur: 0.14 },
          { freq: 1047, at: 0.36, dur: 0.28 },
        ],
        0.7,
      );
      break;
    case "defeat":
      // Descending sawtooth sting.
      sequence(
        ac,
        [
          { freq: 392, at: 0, dur: 0.2, type: "sawtooth" },
          { freq: 294, at: 0.16, dur: 0.24, type: "sawtooth" },
          { freq: 196, at: 0.34, dur: 0.4, type: "sawtooth" },
        ],
        0.55,
      );
      break;
    case "downed":
      sequence(
        ac,
        [
          { freq: 330, at: 0, dur: 0.18, type: "sawtooth" },
          { freq: 196, at: 0.12, dur: 0.3, type: "sawtooth" },
        ],
        0.55,
      );
      break;
    case "revive":
      sequence(
        ac,
        [
          { freq: 440, at: 0, dur: 0.12 },
          { freq: 660, at: 0.1, dur: 0.18 },
        ],
        0.6,
      );
      break;
    default: {
      const _exhaustive: never = cue;
      void _exhaustive;
    }
  }
}

/** Toggle/set mute (persisted by the caller if desired). */
export function setMuted(value: boolean): void {
  muted = value;
  if (master !== null && ctx !== null) {
    master.gain.setValueAtTime(muted ? 0 : MASTER_VOLUME, ctx.currentTime);
  }
}

export function isMuted(): boolean {
  return muted;
}
